// getShortsIds.js
'use strict';
import { addBreadcrumb } from '@sentry/browser';

import {
  INSERT_CLIP_AFTER_X_SHORTS,
  INSERT_CLIP_EVERY_X_SHORTS,
  INSERT_AD_AFTER_X_SHORTS,
  INSERT_AD_EVERY_X_SHORTS,
  SHORTS_ADS_V2,
  MONGO_OBJECT_ID_REGEX_PATTERN,
} from '../RemoteConfigKeys.js';

import createCachedSelector from '../resource/createCachedSelector.js';
import { encodeUnicode } from '../resource/base64Utils.js';
import { SHORT_AD_ID_PREFIX } from '../resource/getIsShortAdId.js';
import { Severity } from '../resource/sentry.js';

import getListData from '../selector/getListData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getOperationData from '../selector/getOperationData.js';

import { getReturnType } from '../action/fetchFeeds.js';

const defaultArray = [];

/**
 * Select shorts ids by shorts category
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} shortsCategory - shorts category.
 * @param {string} livestreamClipCategory - livestream clip category.
 * @return {array} The shorts ids merged with ads and livestream clips.
 */
const getShortsIds = createCachedSelector(
  (state, shortsCategory) =>
    getListData(state, ['shorts', shortsCategory], 'itemIds'),
  (state, shortsCategory, livestreamClipCategory) =>
    getListData(
      state,
      [
        'home',
        getReturnType({ feedName: livestreamClipCategory }),
        livestreamClipCategory,
      ],
      'itemIds'
    ),
  state => getOperationData(state, ['shorts'], 'firstId'),
  state => getRemoteConfigData(state, INSERT_CLIP_AFTER_X_SHORTS),
  state => getRemoteConfigData(state, INSERT_CLIP_EVERY_X_SHORTS),
  state => getRemoteConfigData(state, SHORTS_ADS_V2),
  state => getRemoteConfigData(state, INSERT_AD_AFTER_X_SHORTS),
  state => getRemoteConfigData(state, INSERT_AD_EVERY_X_SHORTS),
  state => getRemoteConfigData(state, MONGO_OBJECT_ID_REGEX_PATTERN),
  (
    defaultShortsIds = defaultArray,
    livestreamClipIds = defaultArray,
    firstId = '',
    clipOffset = 0,
    clipRepeat = 0,
    adUrlList = defaultArray,
    adOffset = 0,
    adRepeat = 0,
    mongoObjectIdRegexPattern
  ) => {
    const shortsIds = defaultShortsIds.slice();
    if (firstId) {
      const mongoObjectIdRegex = new RegExp(mongoObjectIdRegexPattern, 'i');
      const isMessageId = mongoObjectIdRegex.test(firstId);
      if (!isMessageId) {
        addBreadcrumb({
          category: 'ShortAd',
          level: Severity.Debug,
          message: 'found first id. append first id to shortsIds list',
          data: {
            firstId,
            shortsIds,
          },
        });
      }
      shortsIds.unshift(
        isMessageId ? firstId : `${SHORT_AD_ID_PREFIX}${firstId}`
      );
    }

    if (!livestreamClipIds.length && !adUrlList.length) {
      return shortsIds;
    }

    const encodedShortAdList = adUrlList.map(
      url =>
        `${SHORT_AD_ID_PREFIX}${encodeUnicode({
          str: encodeURIComponent(url),
          encoding: 'base64url',
        })}`
    );

    const result = [];
    let shortIdsIndex = 0;
    while (shortsIds.length >= shortIdsIndex) {
      let source = shortsIds;
      let index = shortIdsIndex;

      // TODO: handle repeat is 0 case
      const resultMinusClipOffset = result.length - clipOffset;
      if (
        livestreamClipIds.length &&
        clipRepeat > 0 &&
        resultMinusClipOffset >= 0 &&
        resultMinusClipOffset % clipRepeat === 0
      ) {
        source = livestreamClipIds;
        const clipDivide = resultMinusClipOffset / clipRepeat;
        index = clipDivide % livestreamClipIds.length;
      }

      // encodedShortAdList priority > livestreamClipIds priority
      const resultMinusAdOffset = result.length - adOffset;
      if (
        encodedShortAdList.length &&
        adRepeat > 0 &&
        resultMinusAdOffset >= 0 &&
        resultMinusAdOffset % adRepeat === 0
      ) {
        source = encodedShortAdList;
        const adDivide = resultMinusAdOffset / adRepeat;
        index = adDivide % encodedShortAdList.length;
      }

      if (source[index]) {
        result.push(source[index]);
      }
      if (source === shortsIds) {
        shortIdsIndex++;
      }
    }

    return result;
  }
)(
  (state, shortsCategory, livestreamClipCategory) =>
    `${shortsCategory}:${livestreamClipCategory}`
);

export default getShortsIds;
